import { AUTH_URL } from 'src/configs';
import request from 'src/request';

/** Matches the API's Joi limits, so the form can stop an over-long field before it posts. */
export const NOTIFICATION_LIMITS = {
  title: 150,
  message: 1000,
  description: 5000,
} as const;

export type NotificationKind = 'individual' | 'all';

export type SendNotificationPayload = {
  kind: NotificationKind;
  title: string;
  message: string;
  description?: string;
  /** Required for `individual`, rejected for `all`. */
  user_id?: string;
  /** Only supported for `individual`; the API rejects it on a broadcast. */
  send_email?: boolean;
};

export type SendNotificationResult = {
  /** Present on an individual send. */
  notification?: { id?: string; title?: string };
  email_sent?: boolean;
  /** Present on a broadcast: how many users it will reach. */
  notified_count?: number;
  kind?: NotificationKind;
};

/**
 * A broadcast returns as soon as the recipient count is known and inserts the
 * rows in the background, so `notified_count` is what it will reach rather than
 * what has already been written.
 */
export const sendAdminNotification = async (payload: SendNotificationPayload) => {
  const response = await request.post(`${AUTH_URL}/v1/admin/notifications`, payload);
  return response.data as SendNotificationResult;
};

// ---------------------------------------------------------------------------
// Campaigns — audience-based sends with a per-user delivery log.
// See docs/admin-notifications-api.md in the API repo.
// ---------------------------------------------------------------------------

export type CampaignStatus = 'PREPARING' | 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'CANCELED';
export type DeliveryStatus = 'QUEUED' | 'DELIVERED' | 'FAILED' | 'SKIPPED';
export type DeliveryEmailStatus = 'NOT_REQUESTED' | 'SENT' | 'FAILED' | 'NO_ADDRESS';
export type AudienceType = 'all' | 'country' | 'kyc' | 'users';

export type CampaignAudience = {
  type: AudienceType;
  country_codes?: string[];
  kyc_statuses?: string[];
  kyc_in_progress?: boolean;
  user_ids?: string[];
};

export type CampaignCounts = {
  targeted: number;
  queued: number;
  delivered: number;
  failed: number;
  skipped: number;
  email_sent: number;
  email_failed: number;
};

export type NotificationCampaign = {
  id: string;
  title: string;
  message: string;
  description?: string | null;
  channels: { in_app: boolean; email: boolean };
  audience: CampaignAudience;
  status: CampaignStatus | string;
  counts: CampaignCounts;
  created_by?: string | null;
  created_by_email?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  canceled_at?: string | null;
  last_error?: string | null;
  created_at: string;
  updated_at?: string;
};

export type NotificationDelivery = {
  id: string;
  campaign: string;
  user: string;
  email?: string | null;
  name?: { first?: string; last?: string } | null;
  status: DeliveryStatus | string;
  email_status: DeliveryEmailStatus | string;
  notification?: string | null;
  attempts: number;
  error?: string | null;
  delivered_at?: string | null;
  created_at: string;
};

export type CampaignInput = {
  title: string;
  message: string;
  description?: string;
  channels: { email: boolean };
  audience: CampaignAudience;
};

const buildQuery = (params: Record<string, string | number | boolean | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
};

const BASE = () => `${AUTH_URL}/v1/admin/notifications/campaigns`;

export const previewCampaignAudience = async (audience: CampaignAudience) => {
  const response = await request.post(`${BASE()}/preview`, { audience });
  return response.data as {
    targeted: number;
    sample: { id: string; email?: string | null; name?: { first?: string; last?: string } | null }[];
  };
};

export const createCampaign = async (input: CampaignInput) => {
  const response = await request.post(BASE(), input);
  return response.data.campaign as NotificationCampaign;
};

export const fetchCampaigns = async (params: {
  page?: number;
  limit?: number;
  status?: string;
  audience_type?: string;
  email?: string;
  title?: string;
  created_by?: string;
  from?: string;
  to?: string;
}) => {
  const response = await request.get(`${BASE()}${buildQuery(params)}`);
  return {
    campaigns: (response.data.campaigns ?? []) as NotificationCampaign[],
    meta: response.data.meta as { page: number; limit: number; page_count: number; total_count: number },
  };
};

export const fetchCampaign = async (campaignId: string) => {
  const response = await request.get(`${BASE()}/${campaignId}`);
  return response.data.campaign as NotificationCampaign;
};

export const fetchCampaignDeliveries = async (
  campaignId: string,
  params: { page?: number; limit?: number; status?: string; email_status?: string; email?: string; name?: string },
) => {
  const response = await request.get(`${BASE()}/${campaignId}/deliveries${buildQuery(params)}`);
  return {
    deliveries: (response.data.deliveries ?? []) as NotificationDelivery[],
    meta: response.data.meta as { page: number; limit: number; page_count: number; total_count: number },
  };
};

export const cancelCampaign = async (campaignId: string) => {
  const response = await request.post(`${BASE()}/${campaignId}/cancel`, {});
  return response.data.campaign as NotificationCampaign;
};

export const retryCampaign = async (campaignId: string) => {
  const response = await request.post(`${BASE()}/${campaignId}/retry`, {});
  return response.data as { campaign: NotificationCampaign; requeued: number };
};

export const describeAudience = (audience?: CampaignAudience | null) => {
  if (!audience) return '—';
  switch (audience.type) {
    case 'all':
      return 'All users';
    case 'country':
      return `Country: ${(audience.country_codes ?? []).join(', ') || '—'}`;
    case 'kyc': {
      const parts = [...(audience.kyc_statuses ?? [])];
      if (audience.kyc_in_progress) parts.push('IN PROGRESS');
      return `KYC: ${parts.join(', ') || '—'}`;
    }
    case 'users':
      return `${(audience.user_ids ?? []).length} selected user${(audience.user_ids ?? []).length === 1 ? '' : 's'}`;
    default:
      return String(audience.type);
  }
};

export const campaignErrorMessage = (e: any, fallback: string) =>
  e?.response?.data?.message ?? e?.response?.data?.error ?? e?.response?.data?.details?.[0]?.message ?? fallback;
